import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiPlus, FiSearch } from "react-icons/fi";
import { getRoleRequest, listAllRolesRequest } from "../../services/rolesApi";
import {
  createUserRequest,
  deleteUserRequest,
  getUserRequest,
  listUsersRequest,
  updateUserRequest,
} from "../../services/usersApi";
import Can from "../../components/auth/Can";
import TableRowActions from "../../components/common/TableRowActions";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";

function MembersPage() {
  const { modulePermissions } = usePermissions();
  const perms = modulePermissions("user");
  const showActions = perms.canRead || perms.canUpdate || perms.canDelete;
  const tableColCount = showActions ? 6 : 5;

  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;
  const [allRoles, setAllRoles] = useState([]);
  const [aggregatedPermissions, setAggregatedPermissions] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [isRolesPermissionsLoading, setIsRolesPermissionsLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const normalizeUser = (user) => ({
    id: user?.id,
    name: user?.name || "",
    email: user?.email || "",
    created_at: (user?.created_at || user?.createdAt || "-").slice(0, 10),
    updated_at: (user?.updated_at || user?.updatedAt || "-").slice(0, 10),
  });

  const getUsersFromResponse = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.users)) return payload.users;
    if (Array.isArray(payload?.data?.users)) return payload.data.users;
    return [];
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setIsLoading(true);
        const response = await listUsersRequest({
          search: search.trim() || undefined,
          page,
          per_page: perPage,
        });
        const payload = response.data;
        const normalizedUsers = getUsersFromResponse(payload).map(normalizeUser);
        setUsers(normalizedUsers);

        const fallbackTotal = normalizedUsers.length;
        const resolvedTotal =
          payload?.total ??
          payload?.meta?.total ??
          payload?.data?.total ??
          payload?.data?.meta?.total ??
          fallbackTotal;
        const resolvedLastPage =
          payload?.last_page ??
          payload?.meta?.last_page ??
          payload?.data?.last_page ??
          payload?.data?.meta?.last_page ??
          Math.max(1, Math.ceil(resolvedTotal / perPage));

        setTotalRecords(Number(resolvedTotal) || 0);
        setTotalPages(Number(resolvedLastPage) || 1);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load users.");
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [page, search]);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const resetModalState = () => {
    setForm({ name: "", email: "", password: "" });
    setSelectedRoleIds([]);
    setAggregatedPermissions([]);
    setEditingUserId(null);
    setIsModalOpen(false);
  };

  const openCreateModal = () => {
    setForm({ name: "", email: "", password: "" });
    setSelectedRoleIds([]);
    setAggregatedPermissions([]);
    setEditingUserId(null);
    setIsModalOpen(true);
  };

  const extractRoleIdsFromUserPayload = (payload) => {
    const user = payload?.data ?? payload;
    if (Array.isArray(user?.role_ids)) {
      return user.role_ids.map(Number).filter(Boolean);
    }
    if (Array.isArray(user?.roles)) {
      return user.roles.map((r) => Number(r.id)).filter(Boolean);
    }
    if (user?.role?.id) {
      return [Number(user.role.id)];
    }
    if (user?.role_id) {
      return [Number(user.role_id)];
    }
    return [];
  };

  const openEditModal = async (user) => {
    setEditingUserId(user.id);
    setIsModalOpen(true);
    setForm({
      name: user.name,
      email: user.email,
      password: "",
    });
    setSelectedRoleIds([]);
    setAggregatedPermissions([]);

    try {
      const response = await getUserRequest(user.id);
      const payload = response.data?.data ?? response.data;
      setSelectedRoleIds(extractRoleIdsFromUserPayload({ data: payload }));
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load user for editing.",
      );
      setSelectedRoleIds(extractRoleIdsFromUserPayload({ data: user }));
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const loadModalOptions = async () => {
      try {
        const [rolesResponse] = await Promise.all([listAllRolesRequest()]);
        const rolesData = Array.isArray(rolesResponse.data?.data)
          ? rolesResponse.data.data
          : Array.isArray(rolesResponse.data)
            ? rolesResponse.data
            : [];
        setAllRoles(rolesData);
      } catch (error) {
        toast.error("Failed to load roles.");
      }
    };
    loadModalOptions();
  }, []);

  const toggleRoleSelection = (roleId) => {
    const id = Number(roleId);
    if (!id) return;
    setSelectedRoleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    if (selectedRoleIds.length === 0) {
      setAggregatedPermissions([]);
      return;
    }

    const loadAggregatedPermissions = async () => {
      try {
        setIsRolesPermissionsLoading(true);
        const results = await Promise.all(
          selectedRoleIds.map((id) => getRoleRequest(id)),
        );
        const merged = new Map();
        results.forEach((response) => {
          const rolePayload = response.data?.data || response.data || {};
          const perms = Array.isArray(rolePayload.permissions)
            ? rolePayload.permissions
            : [];
          perms.forEach((p) => {
            const pid = Number(p.id);
            if (pid && !merged.has(pid)) {
              merged.set(pid, p);
            }
          });
        });
        setAggregatedPermissions(
          [...merged.values()].sort((a, b) =>
            (a.name || "").localeCompare(b.name || ""),
          ),
        );
      } catch {
        setAggregatedPermissions([]);
      } finally {
        setIsRolesPermissionsLoading(false);
      }
    };

    loadAggregatedPermissions();
  }, [selectedRoleIds]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const uniqueRoleIds = [...new Set(selectedRoleIds)].filter(Boolean);
    if (uniqueRoleIds.length === 0) {
      toast.error("Select at least one role.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      role_ids: uniqueRoleIds,
    };

    if (form.password.trim()) {
      payload.password = form.password.trim();
    }

    if (!editingUserId && !payload.password) {
      toast.error("Password is required for creating a user.");
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingUserId) {
        const response = await updateUserRequest(editingUserId, payload);
        const updatedUser = normalizeUser(response.data?.data || response.data);
        setUsers((prev) =>
          prev.map((user) =>
            user.id === editingUserId ? { ...user, ...updatedUser } : user,
          ),
        );
        toast.success("User updated successfully.");
      } else {
        const response = await createUserRequest(payload);
        const createdUser = normalizeUser(response.data?.data || response.data);
        setUsers((prev) => [createdUser, ...prev]);
        setPage(1);
        toast.success("User created successfully.");
      }
      resetModalState();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (editingUserId ? "Failed to update user." : "Failed to create user."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const askDeleteUser = (userId) => {
    setDeleteUserId(userId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;
    try {
      await deleteUserRequest(deleteUserId);
      setUsers((prev) => prev.filter((user) => user.id !== deleteUserId));
      toast.success("User deleted.");
      setIsDeleteModalOpen(false);
      setDeleteUserId(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete user.");
    }
  };

  const openViewModal = async (user) => {
    try {
      const response = await getUserRequest(user.id);
      const payload = response.data?.data ?? response.data ?? {};
      const roles = Array.isArray(payload?.roles)
        ? payload.roles.map((role) => role?.name || `Role #${role?.id}`)
        : payload?.role?.name
          ? [payload.role.name]
          : [];
      const roleIds = Array.isArray(payload?.roles)
        ? payload.roles.map((role) => Number(role?.id)).filter(Boolean)
        : payload?.role?.id
          ? [Number(payload.role.id)]
          : Array.isArray(payload?.role_ids)
            ? payload.role_ids.map(Number).filter(Boolean)
            : payload?.role_id
              ? [Number(payload.role_id)]
              : [];
      const directPermissions = Array.isArray(payload?.permissions)
        ? payload.permissions
        : Array.isArray(payload?.user_permissions)
          ? payload.user_permissions
          : Array.isArray(payload?.permission)
            ? payload.permission
            : [];
      const rolePermissions = Array.isArray(payload?.roles)
        ? payload.roles.flatMap((role) =>
            Array.isArray(role?.permissions) ? role.permissions : [],
          )
        : payload?.role && Array.isArray(payload.role?.permissions)
          ? payload.role.permissions
          : [];
      const permissionNames = [...directPermissions, ...rolePermissions]
        .map((permission) => permission?.name || permission?.slug)
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index);

      let resolvedPermissionNames = permissionNames;
      if (resolvedPermissionNames.length === 0 && roleIds.length > 0) {
        const roleResponses = await Promise.all(roleIds.map((id) => getRoleRequest(id)));
        const fallbackPermissions = roleResponses.flatMap((roleResponse) => {
          const rolePayload = roleResponse.data?.data || roleResponse.data || {};
          return Array.isArray(rolePayload.permissions) ? rolePayload.permissions : [];
        });
        resolvedPermissionNames = fallbackPermissions
          .map((permission) => permission?.name || permission?.slug)
          .filter(Boolean)
          .filter((value, index, arr) => arr.indexOf(value) === index);
      }
      setViewingUser({
        ...normalizeUser(payload),
        roles,
        permissions: resolvedPermissionNames,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load user details.");
      setViewingUser({
        ...user,
        roles: [],
        permissions: [],
      });
    }
  };

  return (
    <main className="w-full min-w-0 bg-base-100">
      <section className="w-full border-b border-base-200 bg-base-100">
        <div className="border-b border-base-200 px-4 py-8 sm:px-8">
          <h1 className="ui-page-title tracking-tight">Users</h1>
          <p className="ui-muted mt-2">
            Create, edit, and manage users with name, email, and password.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <label className="input input-bordered relative flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-xl border-base-300 bg-white pl-4 pr-3 sm:max-w-xl">
            <FiSearch className="h-4 w-4 shrink-0 text-base-content/45" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              className="grow bg-transparent outline-none placeholder:text-base-content/40 ui-nav-text"
              placeholder="Search by name or email..."
              autoComplete="off"
            />
          </label>
          <Can permission={PERMISSIONS.USER_CREATE}>
            <button type="button" className="ui-create-btn" onClick={openCreateModal}>
              <FiPlus className="ui-create-btn-icon" />
              Create user
            </button>
          </Can>
        </div>

        <div className="overflow-x-auto border-t border-base-200 px-4 sm:px-8">
          <table className="table table-zebra table-pin-rows table-md min-w-[840px] w-full">
            <thead className="border-b border-base-300 bg-base-200/70">
              <tr>
                <th scope="col" className="w-14 ui-table-header">
                  ID
                </th>
                <th scope="col" className="min-w-[12rem] ui-table-header">
                  NAME
                </th>
                <th scope="col" className="min-w-[14rem] ui-table-header">
                  EMAIL
                </th>
                <th scope="col" className="min-w-[8rem] ui-table-header whitespace-nowrap">
                  CREATED AT
                </th>
                <th scope="col" className="min-w-[8rem] ui-table-header whitespace-nowrap">
                  UPDATED AT
                </th>
                {showActions ? (
                  <th scope="col" className="ui-table-header ui-table-actions-col">
                    ACTIONS
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {Array.from({ length: tableColCount }).map((__, innerIndex) => (
                        <td key={`cell-${innerIndex}`}>
                          <div className="skeleton my-2 h-4 w-full rounded-md" />
                        </td>
                      ))}
                    </tr>
                  ))
                : users.map((user) => (
                    <tr key={user.id} className="transition-colors">
                      <td className="tabular-nums text-base-content/70 ui-nav-text">{user.id}</td>
                      <td className="ui-nav-text font-semibold text-base-content">{user.name}</td>
                      <td className="ui-nav-text text-base-content/85">{user.email}</td>
                      <td className="tabular-nums text-base-content/80 ui-nav-text">{user.created_at}</td>
                      <td className="tabular-nums text-base-content/80 ui-nav-text">{user.updated_at}</td>
                      {showActions ? (
                        <td className="ui-table-actions-col">
                          <TableRowActions
                            readPermission={PERMISSIONS.USER_READ}
                            updatePermission={PERMISSIONS.USER_UPDATE}
                            deletePermission={PERMISSIONS.USER_DELETE}
                            onView={() => openViewModal(user)}
                            onEdit={() => openEditModal(user)}
                            onDelete={() => askDeleteUser(user.id)}
                            viewLabel={`View ${user.name}`}
                            editLabel={`Edit ${user.name}`}
                            deleteLabel={`Delete ${user.name}`}
                          />
                        </td>
                      ) : null}
                    </tr>
                  ))}
              {!isLoading && users.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="py-12 text-center">
                    <div className="alert inline-flex rounded-xl bg-base-200/70">
                      <span className="ui-nav-text">No users found.</span>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-base-200 px-4 py-4 sm:px-8">
          <p className="ui-meta">
            Showing {totalRecords === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, totalRecords)} of {totalRecords} entries
          </p>
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm rounded-l-lg"
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }).map((_, index) => {
              const current = index + 1;
              return (
                <button
                  key={current}
                  type="button"
                  className={`join-item btn btn-sm ${page === current ? "btn-active" : ""}`}
                  onClick={() => setPage(current)}
                >
                  {current}
                </button>
              );
            })}
            <button
              type="button"
              className="join-item btn btn-sm rounded-r-lg"
              disabled={page === totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {isModalOpen ? (
        <dialog open className="modal modal-open">
          <div className="modal-box flex max-h-[92vh] w-[min(94vw,36rem)] max-w-xl flex-col overflow-hidden rounded-2xl bg-white p-0 sm:w-[min(94vw,40rem)] sm:max-w-2xl">
            <div className="shrink-0 border-b border-base-300 bg-white px-5 py-3">
              <h3 className="ui-panel-title">
                {editingUserId ? "Update User" : "Create User"}
              </h3>
              <p className="ui-muted mt-1 text-sm">
                {editingUserId
                  ? "Update profile, roles, and password."
                  : "Add user with roles and credentials."}
              </p>
            </div>

            <form
              className="flex min-h-0 flex-1 flex-col bg-white"
              onSubmit={handleSubmit}
            >
              <div className="ui-modal-scroll-body">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="form-control">
                    <label htmlFor="user-name" className="label">
                      <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                        Name <span className="text-error">*</span>
                      </span>
                    </label>
                    <input
                      id="user-name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                      className="input input-bordered input-sm h-9 min-h-9 rounded-lg bg-white text-xs sm:h-10 sm:text-sm"
                      required
                    />
                  </div>

                  <div className="form-control">
                    <label htmlFor="user-email" className="label">
                      <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                        Email <span className="text-error">*</span>
                      </span>
                    </label>
                    <input
                      id="user-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleFormChange}
                      className="input input-bordered input-sm h-9 min-h-9 rounded-lg bg-white text-xs sm:h-10 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                      Roles <span className="text-error">*</span>
                    </span>
                    <span className="label-text-alt ui-meta">
                      Tap to toggle (multiple allowed)
                    </span>
                  </label>
                  <div className="max-h-[28vh] overflow-auto rounded-lg border border-base-300 p-3">
                    {allRoles.length === 0 ? (
                      <p className="text-sm opacity-60">No roles available.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {allRoles.map((role) => {
                          const id = Number(role.id);
                          const selected = selectedRoleIds.includes(id);
                          return (
                            <button
                              key={id}
                              type="button"
                              onClick={() => toggleRoleSelection(id)}
                              className={[
                                "badge cursor-pointer px-2.5 py-1.5 text-[11px] transition-all duration-200 sm:text-xs",
                                selected
                                  ? "badge-primary hover:brightness-95 hover:shadow-sm"
                                  : "badge-outline border-base-300 bg-white hover:border-primary/50 hover:bg-primary/10 hover:text-primary hover:shadow-sm",
                              ].join(" ")}
                              title={role.name}
                            >
                              {role.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-control">
                  <label htmlFor="user-password" className="label">
                    <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                      Password{" "}
                      {!editingUserId ? (
                        <span className="text-error">*</span>
                      ) : (
                        <span className="ui-meta text-xs font-normal">
                          optional
                        </span>
                      )}
                    </span>
                  </label>
                  <input
                    id="user-password"
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleFormChange}
                    className="input input-bordered input-sm h-9 min-h-9 rounded-lg bg-white text-xs sm:h-10 sm:text-sm"
                    placeholder={
                      editingUserId ? "Leave blank to keep current password" : ""
                    }
                    required={!editingUserId}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                      Permissions preview
                    </span>
                    <span className="label-text-alt ui-meta">
                      Union of selected roles
                    </span>
                  </label>
                  <div className="max-h-[32vh] min-h-[4rem] overflow-auto rounded-lg border border-base-300 p-3">
                    {selectedRoleIds.length === 0 ? (
                      <p className="text-sm opacity-60">
                        Select roles to preview permissions.
                      </p>
                    ) : isRolesPermissionsLoading ? (
                      <p className="text-sm opacity-60">Loading permissions...</p>
                    ) : aggregatedPermissions.length === 0 ? (
                      <p className="text-sm opacity-60">
                        No permissions on selected roles.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {aggregatedPermissions.map((permission) => (
                          <span
                            key={permission.id}
                            className="badge badge-outline border-base-300 bg-white px-2.5 py-1.5 text-[11px] sm:text-xs"
                          >
                            {permission.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="shrink-0 border-t border-base-200 bg-white px-5 py-3.5">
                <div className="modal-action mt-0">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm h-9 min-h-9 rounded-lg px-4 text-sm"
                    onClick={resetModalState}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`btn btn-primary btn-sm h-9 min-h-9 rounded-lg px-4 text-sm ${isSubmitting ? "btn-disabled" : ""}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? editingUserId
                        ? "Updating..."
                        : "Creating..."
                      : editingUserId
                        ? "Update User"
                        : "Create User"}
                  </button>
                </div>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={resetModalState}>
              close
            </button>
          </form>
        </dialog>
      ) : null}

      {viewingUser ? (
        <dialog open className="modal modal-open">
          <div className="ui-detail-modal-box">
            <div className="ui-detail-modal-header">
              <h3 className="ui-modal-title">{viewingUser.name}</h3>
              <p className="ui-muted mt-1">{viewingUser.email} · User profile</p>
            </div>
            <div className="ui-detail-grid">
              <div className="ui-detail-card">
                <p className="ui-meta">ID</p>
                <p className="ui-detail-value-strong">
                  {viewingUser.id}
                </p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Name</p>
                <p className="ui-detail-value-strong">
                  {viewingUser.name}
                </p>
              </div>
              <div className="ui-detail-card sm:col-span-2">
                <p className="ui-meta">Email</p>
                <p className="ui-detail-value">{viewingUser.email}</p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Roles</p>
                {viewingUser.roles?.length ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {viewingUser.roles.map((role) => (
                      <span key={role} className="badge badge-primary badge-sm">
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="ui-detail-value">-</p>
                )}
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Permissions</p>
                {viewingUser.permissions?.length ? (
                  <div className="mt-2 flex max-h-28 flex-wrap gap-2 overflow-auto pr-1">
                    {viewingUser.permissions.map((permission) => (
                      <span
                        key={permission}
                        className="badge badge-outline border-base-300 bg-base-100 badge-sm"
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="ui-detail-value">-</p>
                )}
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Created At</p>
                <p className="ui-detail-value">{viewingUser.created_at}</p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Updated At</p>
                <p className="ui-detail-value">{viewingUser.updated_at}</p>
              </div>
            </div>
            <div className="modal-action mt-0 border-t border-base-200 px-6 py-4">
              <button
                type="button"
                className="btn btn-primary rounded-xl px-6 text-primary-content"
                onClick={() => setViewingUser(null)}
              >
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setViewingUser(null)}>
              close
            </button>
          </form>
        </dialog>
      ) : null}

      {isDeleteModalOpen ? (
        <dialog open className="modal modal-open">
          <div className="modal-box max-w-md rounded-2xl bg-white p-0">
            <div className="border-b border-base-300 px-6 py-5">
              <h3 className="ui-modal-title">Delete User</h3>
              <p className="ui-muted mt-2">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
            </div>
            <div className="modal-action mt-0 border-t border-base-200 px-6 py-4">
              <button
                type="button"
                className="btn btn-ghost rounded-xl"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteUserId(null);
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-error rounded-xl" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setIsDeleteModalOpen(false)}>
              close
            </button>
          </form>
        </dialog>
      ) : null}
    </main>
  );
}

export default MembersPage;
